import { Box, Tooltip, IconButton, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { CustomColumnGridConfig } from '../types'
import dayjs from 'dayjs'
import { useAlertIndicationStore } from '@/store/notification/alertIndicationStore'
import { formatConsumptionValue } from '@/utils/consumptionFormat'
import MeterLink from '@/components/Table/MeterLink'
export const PERIODIC_FIELD = 'periodic' as const

const AlertCell = ({ alerts }: { alerts: string[] | null }) => {
  if (!alerts || alerts.length === 0) return <span>-</span>
  if (alerts.length === 1) return <span>{alerts[0]}</span>
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
      <Typography noWrap sx={{ fontSize: 'inherit', lineHeight: 'inherit' }}>{alerts[0]}</Typography>
      <Tooltip
        title={
          <Box dir="rtl" sx={{ whiteSpace: 'pre-line', fontSize: '12px' }}>
            {alerts.join('\n')}
          </Box>
        }
        arrow
        placement="top"
      >
        <IconButton size="small" sx={{ p: 0, ml: 0.5 }}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: '#4C84C8' }} />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

const ReplacementCell = ({ dates }: { dates: string[] | null }) => {
  if (!dates || dates.length === 0) return <span>-</span>
  const formatted = dates.map(d => dayjs(d).format('DD/MM/YYYY'))
  if (formatted.length === 1) return <span>הוחלף ב-{formatted[0]}</span>
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
      <Typography noWrap sx={{ fontSize: 'inherit', lineHeight: 'inherit' }}>הוחלף ב-{formatted[0]}</Typography>
      <Tooltip
        title={
          <Box dir="rtl" sx={{ whiteSpace: 'pre-line', fontSize: '12px' }}>
            {formatted.map(d => `הוחלף ב-${d}`).join('\n')}
          </Box>
        }
        arrow
        placement="top"
      >
        <IconButton size="small" sx={{ p: 0, ml: 0.5 }}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: '#4C84C8' }} />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

const getMeterNumberFromUrl = () => {
  const match = window.location.pathname.match(/\/consumers\/([^/]+)/)
  return match ? match[1] : null
}

const getIsMeterMuted = (meterNumberFromRow?: string | number) => {
  const meter = meterNumberFromRow || getMeterNumberFromUrl()
  if (!meter) return false
  try {
    const saved = localStorage.getItem(`consumer-settings-${meter}`)
    if (saved) {
      const parsed = JSON.parse(saved)
      return parsed.alertsMode === 'muted'
    }
  } catch (e) {
    console.error(e)
  }
  return false
}

const periodicColumnsConfig: CustomColumnGridConfig[] = [
  {
    field: 'meterNumber',
    headerName: "מס' מד",
    flex: 1,
    renderCell: (params: any) =>
      params.value != null ? <MeterLink meter={params.value} /> : '-',
    filter: { type: 'search' },
    filterable: true,
  },
  {
    field: 'period',
    headerName: 'תקופה',
    flex: 1,
    filter: { type: 'options' },
    filterable: true,
    valueGetter: (_value, row) => row?.periodRange || (row?.period ? dayjs(row.period).format('DD/MM/YYYY') : ''),
    sortComparator: (v1, v2) => new Date(v1).getTime() - new Date(v2).getTime(),
    renderCell: (params: any) => {
      // `period` remains raw for filtering; `periodRange` is display-only.
      if (params.row?.periodRange) return params.row.periodRange
      if (!params.value) return '-'
      return dayjs(params.value).format('DD/MM/YYYY')
    },
  },

  {
    field: 'consumption',
    headerName: 'צריכה',
    flex: 1,
    filter: { type: 'aggregated' },
    filterable: true,
    sortComparator: (v1: any, v2: any) => (Number(v1) || 0) - (Number(v2) || 0),
    renderCell: (params: any) => formatConsumptionValue(params.row?.consumption),
  },
  {
    field: 'alerts',
    headerName: 'התראה',
    flex: 1.2,
    minWidth: 140,
    filter: { type: 'options' },
    filterable: true,
    sortable: true,
    valueGetter: (_value, row) => {
      const rawAlert = row?.alerts || row?.alert
      if (Array.isArray(rawAlert)) return rawAlert.join(', ')
      return String(rawAlert ?? '')
    },
    sortComparator: (v1: any, v2: any) => String(v1 ?? '').localeCompare(String(v2 ?? ''), 'he'),
    renderCell: (params: any) => {
      const rawAlert = params.row?.alerts || params.row?.alert
      const alerts: string[] | null = Array.isArray(rawAlert) ? rawAlert : (rawAlert ? [rawAlert] : null)
      const isMuted = params.row?.isMuted === true || getIsMeterMuted(params.row?.meterNumber)
      const showIndication = useAlertIndicationStore.getState().showMutedAlertIndication

      if (!alerts || alerts.length === 0) return '-'

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
          <AlertCell alerts={alerts} />
          {isMuted && showIndication && (
            <Typography
              component="span"
              sx={{ fontSize: '11px', color: '#94A3B8', fontFamily: 'Rubik', fontWeight: 400 }}
            >
              התראה מושתקת
            </Typography>
          )}
        </Box>
      )
    },
  },
  {
    field: 'replacementDates',
    headerName: 'החלפה',
    flex: 1.2,
    minWidth: 140,
    filter: { type: 'none' },
    filterable: false,
    sortable: false,
    renderCell: (params: any) => <ReplacementCell dates={params.value} />,
  },
]

export default periodicColumnsConfig
